/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import type { Product } from '@shared/types/products';
import type { TemplateMetadata, CheckRepoInitializedResult } from '../../../../server/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { MermaidDiagram } from '../../common/MermaidDiagram';
import {
  Edit2,
  FileText,
  Loader2,
  Network,
  Layers,
  Play,
} from 'lucide-react';

export interface ArchitectureTabProps {
  product: Product;
  repoInitStatus: CheckRepoInitializedResult | null;
  templates: TemplateMetadata[];
  loadingTemplates: boolean;
  selectedTemplate: string;
  onTemplateSelect: (template: string) => void;
  isApplyingTemplate: boolean;
  onApplyTemplate: () => void;
  onEdit: (product: Product) => void;
}

export function ArchitectureTab({
  product,
  repoInitStatus,
  templates,
  loadingTemplates,
  selectedTemplate,
  onTemplateSelect,
  isApplyingTemplate,
  onApplyTemplate,
  onEdit,
}: ArchitectureTabProps) {
  const { toast } = useToast();
  const hasDiagram = !!product.architectureDiagram;
  const showInitializeCard = product.repositoryUrl && repoInitStatus && !repoInitStatus.initialized;

  return (
    <div className="space-y-6">
      {/* Initialize Codebase Card - shown when repo is blank */}
      {showInitializeCard && (
        <Card className="border-dashed border-2">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" />
              <CardTitle>Initialize Codebase</CardTitle>
            </div>
            <CardDescription>
              Apply an architecture template to set up the project structure
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Your repository is ready but doesn't have any code yet. Select an architecture 
                template below to generate the initial project scaffolding with best practices.
              </p>

              {loadingTemplates ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading templates...
                </div>
              ) : templates.length > 0 ? (
                <div className="grid gap-3">
                  {/* Quick Template Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    {templates.slice(0, 4).map((template) => (
                      <Button
                        key={template.name}
                        variant={selectedTemplate === template.name ? 'default' : 'outline'}
                        className="h-auto py-3 px-4 justify-start"
                        onClick={() => onTemplateSelect(template.name)}
                        disabled={isApplyingTemplate}
                      >
                        <div className="text-left">
                          <div className="font-medium text-sm">{template.displayName}</div>
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {template.features.frontend && 'Frontend'}
                            {template.features.frontend && template.features.backend && ' + '}
                            {template.features.backend && 'Backend'}
                            {template.features.database && ' + DB'}
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>

                  {selectedTemplate && (
                    <div className="p-3 bg-muted rounded-md">
                      {(() => {
                        const template = templates.find(t => t.name === selectedTemplate);
                        return template ? (
                          <div className="text-sm">
                            <p className="font-medium">{template.displayName}</p>
                            <p className="text-muted-foreground mt-1">{template.description}</p>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  <Button
                    onClick={onApplyTemplate}
                    disabled={!selectedTemplate || isApplyingTemplate}
                    className="gap-2"
                  >
                    {isApplyingTemplate ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Initializing Codebase...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4" />
                        Initialize with {selectedTemplate ? templates.find(t => t.name === selectedTemplate)?.displayName : 'Template'}
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No templates available.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Network className="h-5 w-5" />
            <CardTitle>Architecture Diagram</CardTitle>
          </div>
          <CardDescription>
            Visual representation of the system architecture using Mermaid diagrams
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {hasDiagram ? (
            <>
              {/* Rendered Diagram */}
              <div className="border rounded-lg p-6 bg-white min-h-[300px]">
                <MermaidDiagram 
                  diagram={product.architectureDiagram!} 
                  className="w-full"
                />
              </div>

              <Separator />

              {/* Diagram Source */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Diagram Source (Mermaid)
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(product.architectureDiagram || '');
                      toast({
                        title: 'Copied to clipboard',
                        description: 'Diagram source has been copied.',
                      });
                    }}
                  >
                    Copy Source
                  </Button>
                </div>
                <pre className="p-4 bg-muted rounded-lg text-sm font-mono overflow-x-auto max-h-48">
                  {product.architectureDiagram}
                </pre>
              </div>

              <Separator />

              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onEdit(product)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Diagram
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Network className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No Architecture Diagram</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                This product doesn't have an architecture diagram yet. You can add one by editing 
                the product or using the Architecture Designer agent to generate one automatically.
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => onEdit(product)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Add Diagram Manually
                </Button>
              </div>

              {/* Example Diagram */}
              <div className="mt-8 text-left">
                <p className="text-xs text-muted-foreground mb-2 text-center">
                  Example Mermaid diagram syntax:
                </p>
                <pre className="p-4 bg-muted rounded-lg text-xs font-mono text-left overflow-x-auto max-w-lg mx-auto">
{`graph TD
    A[Client] --> B[API Gateway]
    B --> C[Backend Service]
    C --> D[(Database)]`}
                </pre>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
